import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getGatewaySettings, saveGatewaySettings, paymentError } from "../api/payments";
import { gatewayUrlDefaults } from "../lib/gateway-url-defaults";

const draftFor = (value, provider) => ({ ...gatewayUrlDefaults(value, provider), testApiKey: "", liveApiKey: "", clearTestKey: false, clearLiveKey: false });
export default function useGatewaySettings({ read = getGatewaySettings, write = saveGatewaySettings, provider = "multisafepay" } = {}) {
  const token = useSelector((state) => state.user.token);
  const [draft, setDraft] = useState(null), [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [error, setError] = useState(""), [saved, setSaved] = useState(false), [attempt, setAttempt] = useState(0);
  const generation = useRef(0), pending = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    const requestGeneration = generation;
    generation.current++; pending.current = false; setSaving(false);
    setLoading(true); setError(""); setDraft(null); setSaved(false);
    read(token, controller.signal).then((value) => {
      if (!controller.signal.aborted) setDraft(draftFor(value, provider));
    }).catch((reason) => { if (!controller.signal.aborted) setError(paymentError(reason)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); requestGeneration.current++; };
  }, [token, attempt, read, provider]);
  async function save() {
    if (!draft || loading || pending.current) return;
    const current = generation.current; pending.current = true;
    setSaving(true); setError(""); setSaved(false);
    try {
      const value = await write(token, draft);
      if (generation.current === current) { setDraft(draftFor(value, provider)); setSaved(true); }
    } catch (reason) { if (generation.current === current) setError(paymentError(reason)); }
    finally { if (generation.current === current) { pending.current = false; setSaving(false); } }
  }
  function change(key, value) { setSaved(false); setDraft((current) => ({ ...current, [key]: value })); }
  return { draft, loading, saving, error, saved, change, save, reload: () => setAttempt((value) => value + 1) };
}
